import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { findCanvasTitle } from "../editor/canvasTitle";
import { classPath } from "../router";

import { useFrameImages } from "./useFrameImages";
import { fileIdsOfFrame, loadFiles, prepareScene } from "./viewerData";

import type { RefObject } from "react";
import type { ApiErrorCode } from "../api/client";
import type { EditorRoute } from "../router";
import type { ExcalidrawFrameLikeElement } from "@excalidraw/excalidraw/element/types";
import type { BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ViewerScene } from "./viewerData";

type LoadState =
  | { status: "loading" }
  | { status: "failed"; code: ApiErrorCode }
  | { status: "ready"; scene: ViewerScene };

/** Keeps the page fixed: no pinch zoom, no double-tap zoom, no ctrl+wheel. */
const useZoomBlock = (target: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const element = target.current;
    if (!element) {
      return;
    }
    const prevent = (event: Event) => event.preventDefault();
    const preventMultiTouch = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventCtrlWheel = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };
    const options = { passive: false } as const;
    element.addEventListener("gesturestart", prevent, options);
    element.addEventListener("gesturechange", prevent, options);
    element.addEventListener("touchmove", preventMultiTouch, options);
    element.addEventListener("wheel", preventCtrlWheel, options);
    return () => {
      element.removeEventListener("gesturestart", prevent);
      element.removeEventListener("gesturechange", prevent);
      element.removeEventListener("touchmove", preventMultiTouch);
      element.removeEventListener("wheel", preventCtrlWheel);
    };
  }, [target]);
};

/**
 * Phone-only, read-only view of a canvas: its frames one per screen, swiped
 * left and right. The state is read once on open — a snapshot, no sockets and
 * no live updates.
 */
export const CanvasViewer = ({ route }: { route: EditorRoute }) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { api } = auth;
  const { classId, canvasId } = route;

  const rootRef = useRef<HTMLDivElement>(null);
  const pagerRef = useRef<HTMLDivElement>(null);
  const indexRowRef = useRef<HTMLDivElement>(null);
  useZoomBlock(rootRef);

  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [title, setTitle] = useState<string | null>(null);
  const [current, setCurrent] = useState(0);

  const filesRef = useRef<BinaryFiles>({});
  const currentRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadState = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const state = await api.getCanvasState(classId, canvasId);
      if (!mountedRef.current) {
        return;
      }
      filesRef.current = {};
      setLoad({
        status: "ready",
        scene: prepareScene(state),
      });
    } catch (error) {
      if (mountedRef.current) {
        setLoad({
          status: "failed",
          code: authRef.current.handleError(error),
        });
      }
    }
  }, [api, classId, canvasId]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  useEffect(() => {
    let cancelled = false;
    void findCanvasTitle(api, classId, canvasId).then((found) => {
      if (!cancelled) {
        setTitle(found);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [api, classId, canvasId]);

  const scene = load.status === "ready" ? load.scene : null;
  const frames = useMemo(() => scene?.frames ?? [], [scene]);

  // Render the current frame first, then its neighbours; keep only a few
  // images so a long canvas does not fill a phone's memory.
  const imageSource = useMemo(
    () =>
      scene
        ? {
            elements: scene.elements,
            frames,
            filesRef,
            lazy: {
              fileIdsOf: (frame: ExcalidrawFrameLikeElement) =>
                fileIdsOfFrame(scene, frame),
              load: (
                fileIds: readonly string[],
                files: BinaryFiles,
                isCancelled: () => boolean,
              ) =>
                loadFiles(
                  api,
                  { classId, canvasId },
                  fileIds,
                  files,
                  isCancelled,
                ),
            },
          }
        : null,
    [api, classId, canvasId, scene, frames],
  );
  const { urls, failedFrames } = useFrameImages(imageSource, current);

  // The page index follows the swipe, read from the pager's scroll position.
  const frameRequest = useRef(0);
  const onScroll = () => {
    if (frameRequest.current) {
      return;
    }
    frameRequest.current = requestAnimationFrame(() => {
      frameRequest.current = 0;
      const pager = pagerRef.current;
      if (pager && pager.clientWidth > 0) {
        const index = Math.round(pager.scrollLeft / pager.clientWidth);
        setCurrent(Math.max(0, Math.min(frames.length - 1, index)));
      }
    });
  };
  useEffect(
    () => () => {
      if (frameRequest.current) {
        cancelAnimationFrame(frameRequest.current);
      }
    },
    [],
  );

  // A rotation changes the slide width; stay on the same frame.
  useEffect(() => {
    const onResize = () => {
      const pager = pagerRef.current;
      if (pager) {
        pager.scrollLeft = currentRef.current * pager.clientWidth;
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const jumpTo = (index: number) => {
    const pager = pagerRef.current;
    if (pager) {
      // Assigned, not animated: a smooth scroll would sweep through (and
      // render) every frame in between.
      pager.scrollLeft = index * pager.clientWidth;
    }
    setCurrent(index);
  };

  // Keep the active number visible in the index row.
  useEffect(() => {
    const row = indexRowRef.current;
    const button = row?.children[current] as HTMLElement | undefined;
    if (row && button) {
      row.scrollLeft =
        button.offsetLeft - (row.clientWidth - button.offsetWidth) / 2;
    }
  }, [current, frames.length]);

  const frameName = (index: number) =>
    frames[index]?.name?.trim() || `프레임 ${index + 1}`;

  return (
    <div className="canvaschool-viewer" ref={rootRef}>
      <header className="canvaschool-viewer-bar">
        <Link className="canvaschool-viewer-back" to={classPath(classId)}>
          ← 학급
        </Link>
        {title && <h1 className="canvaschool-viewer-title">{title}</h1>}
      </header>
      {load.status === "loading" && (
        <p className="canvaschool-viewer-note" aria-busy="true">
          캔버스를 불러오는 중…
        </p>
      )}
      {load.status === "failed" && (
        <div className="canvaschool-viewer-note">
          <ErrorAlert code={load.code} />
          <button type="button" onClick={() => void loadState()}>
            다시 시도
          </button>{" "}
          <Link to={classPath(classId)}>학급으로 돌아가기</Link>
        </div>
      )}
      {scene && frames.length === 0 && (
        <p className="canvaschool-viewer-note">
          아직 볼 수 있는 프레임이 없어요.
        </p>
      )}
      {scene && frames.length > 0 && (
        <>
          <nav
            className="canvaschool-viewer-index"
            aria-label="프레임 번호"
            ref={indexRowRef}
          >
            {frames.map((frame, index) => (
              <button
                key={frame.id}
                type="button"
                className="canvaschool-viewer-index-button"
                aria-label={`${index + 1}번째 프레임`}
                aria-current={index === current ? "true" : undefined}
                onClick={() => jumpTo(index)}
              >
                {index + 1}
              </button>
            ))}
          </nav>
          <p className="canvaschool-viewer-caption" aria-live="polite">
            {frameName(current)}
          </p>
          <div
            className="canvaschool-viewer-pager"
            ref={pagerRef}
            onScroll={onScroll}
          >
            {frames.map((frame, index) => (
              <section key={frame.id} className="canvaschool-viewer-slide">
                {urls[frame.id] ? (
                  <img
                    className="canvaschool-viewer-image"
                    src={urls[frame.id]}
                    alt={frameName(index)}
                    draggable={false}
                  />
                ) : failedFrames.has(frame.id) ? (
                  <p className="canvaschool-viewer-note">
                    이 프레임을 그리지 못했어요.
                  </p>
                ) : (
                  <p className="canvaschool-viewer-note" aria-busy="true">
                    그리는 중…
                  </p>
                )}
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
